import { FilesetResolver, FaceDetector, FaceDetectorResult } from '@mediapipe/tasks-vision'

interface FacePosition {
  x: number
  y: number
  z: number
  size: number  // Face size (0-1 normalized, larger = closer to camera)
}

export class FaceTracker {
  private faceDetector: FaceDetector | null = null
  private video: HTMLVideoElement | null = null
  private lastFaces: FacePosition[] = []
  private isProcessing = false

  async initialize(): Promise<void> {
    // Get video element
    this.video = document.getElementById('webcam') as HTMLVideoElement
    if (!this.video) {
      throw new Error('Video element not found')
    }

    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    })

    this.video.srcObject = stream
    await this.video.play()

    // Initialize MediaPipe FaceDetector
    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    )

    this.faceDetector = await FaceDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite',
        delegate: 'GPU'
      },
      runningMode: 'VIDEO',
      minDetectionConfidence: 0.5
    })

    // Start detection loop
    this.detectFaces()
  }

  private detectFaces = (): void => {
    if (!this.faceDetector || !this.video || this.isProcessing) {
      requestAnimationFrame(this.detectFaces)
      return
    }

    this.isProcessing = true

    const now = performance.now()
    const results: FaceDetectorResult = this.faceDetector.detectForVideo(this.video, now)

    // Extract face centers from bounding boxes
    this.lastFaces = results.detections.slice(0, 5).map(detection => {
      const bbox = detection.boundingBox
      if (!bbox) {
        return { x: 0.5, y: 0.5, z: 0, size: 0.2 }
      }

      // Calculate center of bounding box (normalized 0-1)
      const videoWidth = this.video!.videoWidth || 640
      const videoHeight = this.video!.videoHeight || 480

      const centerX = (bbox.originX + bbox.width / 2) / videoWidth
      const centerY = (bbox.originY + bbox.height / 2) / videoHeight

      // Face size based on bounding box (larger = closer to camera)
      const faceSize = Math.max(bbox.width / videoWidth, bbox.height / videoHeight)

      return {
        x: centerX,
        y: centerY,
        z: faceSize * 2,  // Depth based on size
        size: faceSize * 1.5  // Scale up for better gravity well size
      }
    })

    this.isProcessing = false
    requestAnimationFrame(this.detectFaces)
  }

  // Same interface as HandTracker for easy swapping
  public getHands(): FacePosition[] {
    return this.lastFaces
  }

  public dispose(): void {
    if (this.video?.srcObject) {
      const stream = this.video.srcObject as MediaStream
      stream.getTracks().forEach(track => track.stop())
    }
    this.faceDetector?.close()
  }
}
